import type Phaser from "phaser";
import { durationMs } from "odyssey-shared";
import type { HudFeed } from "./hud/store.ts";

/**
 * The replies the scene shows on the HUD's message line (P1.9-T13), each for the motion table's `message` time
 * counted on the scene's clock.
 */
export class MessageLine {
  private timer: Phaser.Time.TimerEvent | null = null;

  /**
   * @param scene The scene whose clock times a reply.
   * @param hud The HUD's store, which holds the line.
   */
  public constructor(
    private readonly scene: Phaser.Scene,
    private readonly hud: HudFeed,
  ) {}

  /**
   * Shows a reply for the motion table's `message` (100 frames, 4 s); saying it again, or another reply, restarts
   * the time. When the time runs out the line is cleared only if it still shows this reply, so a line that replaced
   * it stays.
   *
   * @param text The words.
   */
  public say(text: string): void {
    this.hud.setMessage(text);
    this.timer?.remove(false);
    this.timer = this.scene.time.delayedCall(durationMs("message"), () => {
      this.hud.expire(text);
      this.timer = null;
    });
  }

  /**
   * Empties the line at once and stops the running reply's time.
   */
  public clear(): void {
    this.timer?.remove(false);
    this.timer = null;
    this.hud.setMessage(null);
  }
}

/**
 * Shows a line on the HUD's message line for the motion table's `message` time, counted on the page's timers, for
 * callers that hold no scene (a key handler bound before the scene exists). The line is cleared only if it still
 * shows this line, as `MessageLine.say` does.
 *
 * @param hud The HUD's store, which holds the line.
 * @param text The words.
 */
export function sayOnHud(hud: HudFeed, text: string): void {
  hud.setMessage(text);
  setTimeout(() => hud.expire(text), durationMs("message"));
}
